import { CheckIcon, XIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import { InfoRow } from '@/components/layout/info-row'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { ZoomableImage } from '@/components/ui/zoomable-image'
import { getAssetLink } from '@/helpers/get-asset-link'
import { Comment } from '@/models/features/comments.models'
import { formatDateLong, formatTimeOnly } from '@/utils'

import * as S from './comments.styled'

export const getCommentsColumns = ({
  isPending,
  onAproveRejectButtonClick,
  t,
}: {
  isPending: boolean
  onAproveRejectButtonClick: (comment: Comment, approve: boolean) => void
  t: TFunction<['groups']>
}): TableColumn<Comment>[] => [
  {
    id: 'author',
    render: (comment) => {
      const fullName = `${comment.author.firstName} ${comment.author.lastName}`
      return (
        <S.CommentTableAuthorContainer>
          <Avatar
            src={getAssetLink({ file: comment.author.avatar, path: 'chitown-avatar/avatar' })}
            alt={fullName}
            size="small"
          />
          <S.NameContainer>
            <Typography size="text-small" breakLines={2}>
              {fullName}
            </Typography>
            {comment.author.unit && (
              <Typography size="text-small" breakLines={2}>
                {comment.author.unit}
              </Typography>
            )}
          </S.NameContainer>
        </S.CommentTableAuthorContainer>
      )
    },
    title: t('groups:comments.columns.author'),
  },
  {
    id: 'group',
    render: (comment) => (
      <Badge color="green" variant="light" size="small">
        {comment.group.name}
      </Badge>
    ),
    title: t('groups:comments.columns.group'),
  },
  {
    id: 'comment',
    render: (comment) => {
      const imageUrls = comment.images.map((image) =>
        getAssetLink({ file: image, path: 'chitown-images/groups' }),
      )

      return (
        <S.CommentContainer>
          <Typography size="text-small" breakLines={3}>
            {comment.message}
          </Typography>
          {imageUrls.map((src, index) => (
            <ZoomableImage
              key={comment.images[index]}
              alt={t('groups:comments.imageAlt')}
              height="6rem"
              images={imageUrls}
              initialIndex={index}
              src={src}
              title={t('groups:comments.imagePreviewTitle')}
              width="6rem"
            />
          ))}
        </S.CommentContainer>
      )
    },
    title: t('groups:comments.columns.comment'),
    width: '30rem',
  },
  {
    id: 'post',
    render: (comment) => (
      <Typography size="text-small" breakLines={3}>
        {comment.postId.title}
      </Typography>
    ),
    title: t('groups:comments.columns.post'),
  },
  {
    id: 'date',
    render: (comment) => (
      <InfoRow
        title={formatDateLong(comment.createdAt)}
        subtitle={formatTimeOnly(comment.createdAt)}
      />
    ),
    sortKey: 'createdAt',
    title: t('groups:comments.columns.date'),
  },
  {
    id: 'actions',
    render: (comment) => (
      <S.ButtonsContainer>
        <S.CircularButton
          $isDanger
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation()
            onAproveRejectButtonClick(comment, false)
          }}
        >
          <XIcon size={16} />
        </S.CircularButton>
        <S.CircularButton
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation()
            onAproveRejectButtonClick(comment, true)
          }}
        >
          <CheckIcon size={16} />
        </S.CircularButton>
      </S.ButtonsContainer>
    ),
    title: t('groups:comments.columns.actions'),
  },
]

export const commentsQueryFiltersSchema = z.object({
  groupId: z.string().optional(),
  limit: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
})

export type CommentsFilters = z.infer<typeof commentsQueryFiltersSchema>

export const commentsInitialFilters: CommentsFilters = {
  groupId: '',
  limit: DEFAULT_LIMIT,
  skip: 0,
  sortBy: 'createdAt',
  sortOrder: 'desc',
}
